import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import {
  BILLING_ERROR_CODES,
  BillingError,
  BillingSubscription,
  CANCEL_REASONS,
  CANCEL_REASON_LABELS,
  CancelReason,
  MAX_CANCEL_DETAILS_LENGTH,
  buildCancelPayload,
  cancelBannerDate,
  cancelSubscription,
} from '../../services/billingService';

interface CancelSubscriptionModalProps {
  open: boolean;
  periodEnd: string | null;
  onClose: () => void;
  onCancelled: (view: BillingSubscription) => void;
  onAlreadyCancelled: () => void;
}

/**
 * Period-end cancel flow. Cancel = scheduled (access kept until period end);
 * no resume/refund logic. Reason is UI-required; the API stays optional.
 */
const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  open,
  periodEnd,
  onClose,
  onCancelled,
  onAlreadyCancelled,
}) => {
  const [reason, setReason] = useState<CancelReason | null>(null);
  const [details, setDetails] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastPayload = useRef<{ reason: CancelReason; details: string } | null>(
    null
  );
  const dateLabel = cancelBannerDate(periodEnd);

  useEffect(() => {
    if (open) {
      setReason(null);
      setDetails('');
      setPending(false);
      setError(null);
      lastPayload.current = null;
    }
  }, [open ]);

  const submit = async (
    picked: CancelReason | null,
    text: string
  ): Promise<void> => {
    if (!picked || pending) return;
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Please log in to manage billing.');
      return;
    }
    lastPayload.current = { reason: picked, details: text };
    setPending(true);
    setError(null);
    try {
      const view = await cancelSubscription(
        token,
        buildCancelPayload(picked, text)
      );
      onCancelled(view);
    } catch (err) {
      if (
        err instanceof BillingError &&
        err.status === 404 &&
        err.code === BILLING_ERROR_CODES.NO_ACTIVE_SUBSCRIPTION
      ) {
        onAlreadyCancelled();
        return;
      }
      setError(
        err instanceof Error ? err.message : 'Failed to cancel subscription.'
      );
    } finally {
      setPending(false);
    }
  };

  const handleRetry = (): void => {
    if (!lastPayload.current) return;
    void submit(lastPayload.current.reason, lastPayload.current.details);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, dialogReason) => {
        if (!pending && dialogReason !== 'backdropClick') onClose();
      }}
      disableEscapeKeyDown={pending}
      aria-labelledby="cancel-subscription-title"
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#17151f',
          backgroundImage: 'none',
          color: '#f7f6fb',
          border: '1px solid rgba(170, 147, 198, 0.24)',
          borderRadius: '12px',
          boxShadow: '0 18px 44px rgba(9, 8, 14, 0.5)',
        },
      }}
    >
      <DialogTitle
        id="cancel-subscription-title"
        sx={{ color: '#f7f6fb', paddingBottom: 0 }}
      >
        Cancel subscription?
      </DialogTitle>
      <DialogContent>
        <p className="billing-status billing-cancel-notice">
          You&apos;ll keep access and your banked pixles until{' '}
          <strong>{dateLabel}</strong>. After that billing stops and
          generation pauses until you resubscribe.
        </p>
        <fieldset className="billing-cancel-reasons">
          <legend>Why are you cancelling?</legend>
          {CANCEL_REASONS.map((value) => (
            <label key={value} className="billing-cancel-reason">
              <input
                type="radio"
                name="cancel-reason"
                value={value}
                checked={reason === value}
                disabled={pending}
                onChange={() => setReason(value)}
              />
              {CANCEL_REASON_LABELS[value] ?? value}
            </label>
          ))}
        </fieldset>
        <textarea
          className="billing-cancel-details"
          placeholder="Anything we should know? (optional)"
          maxLength={MAX_CANCEL_DETAILS_LENGTH}
          value={details}
          disabled={pending}
          onChange={(e) => setDetails(e.target.value)}
          aria-label="Cancellation details (optional)"
        />
        <p className="billing-cancel-counter" aria-live="polite">
          {details.length}/{MAX_CANCEL_DETAILS_LENGTH}
        </p>
        {error && (
          <p className="billing-error" role="alert" aria-live="assertive">
            {error}{' '}
            <button
              type="button"
              className="billing-secondary"
              onClick={handleRetry}
              disabled={pending}
              style={{ minHeight: 32, padding: '4px 12px', marginLeft: 8 }}
            >
              Retry
            </button>
          </p>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px 20px', gap: '12px' }}>
        <button
          type="button"
          className="billing-secondary"
          onClick={onClose}
          disabled={pending}
          autoFocus
        >
          Keep my plan
        </button>
        <button
          type="button"
          className="billing-secondary billing-danger"
          disabled={!reason || pending}
          onClick={() => void submit(reason, details)}
        >
          {pending ? 'Cancelling…' : 'Confirm cancel'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelSubscriptionModal;
